import { ParcRealEstateTenantsDto } from 'api/quote';
import { ExclamationPointIcon } from 'components/icons/customIcons';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'components/icons/Icons';
import { FC, useCallback, useState } from 'react';
import { TenantsDocumentsManager } from './TenantsDocumentsManager';

interface ParcTenantsByRealEstateProps {
  realEstates: ParcRealEstateTenantsDto[];
  refresh: () => void;
}

/**
 * Onglet « Locataires » de la modale risque GLI en mode parc : un accordéon par bien couvert,
 * embarquant le gestionnaire de documents locataires existant (TenantsDocumentsManager) tel quel.
 * Les biens au dossier incomplet sont dépliés par défaut.
 */
export const ParcTenantsByRealEstate: FC<ParcTenantsByRealEstateProps> = ({
  realEstates,
  refresh
}) => {
  const [expandedBiens, setExpandedBiens] = useState<Set<string>>(
    () => new Set(realEstates.filter(bien => !bien.isCompleted).map(bien => bien.id))
  );

  const toggleBien = useCallback((bienId: string) => {
    setExpandedBiens(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bienId)) {
        newSet.delete(bienId);
      } else {
        newSet.add(bienId);
      }
      return newSet;
    });
  }, []);

  return (
    <div className="space-y-3">
      {realEstates.map(bien => {
        const isExpanded = expandedBiens.has(bien.id);
        const tenantsCount = bien.tenants.length;

        return (
          <div key={bien.id} className="border rounded-2xl shadow overflow-hidden bg-white">
            {/* En-tête du bien */}
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleBien(bien.id)}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {bien.isCompleted ? (
                  <div className="p-1 rounded-full bg-green-500 text-white shrink-0">
                    <CheckIcon className="w-3 h-3" />
                  </div>
                ) : (
                  <div className="p-1 rounded-full bg-orange-500 text-white shrink-0">
                    <ExclamationPointIcon className="w-3 h-3" />
                  </div>
                )}
                <div className="min-w-0">
                  <span className="font-medium truncate block">{bien.name}</span>
                  {bien.address && (
                    <span className="text-sm text-gray-500 truncate block">
                      {[bien.address.line1, bien.address.zipCode, bien.address.city]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {tenantsCount} locataire{tenantsCount > 1 ? 's' : ''}
                </div>
                {isExpanded ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Dossiers documentaires des locataires du bien */}
            {isExpanded && (
              <div className="border-t bg-gray-50 p-4">
                {tenantsCount > 0 ? (
                  <TenantsDocumentsManager refresh={refresh} tenants={bien.tenants} title="" />
                ) : (
                  <p className="text-center py-4 text-sm text-gray-500">
                    Aucun locataire actif sur ce bien
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
